import { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import {
  Stack,
  Typography,
  Container,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider
} from '@mui/material';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { EntityContext } from '../entity';
import { AccountContext } from '../account';
import { createWebPKI, digestAlgForWebPki } from '../utils/webpki';

const CERT_DATE_FIELDS = ['validityEnd', 'validityEndDate', 'notAfter', 'validTo', 'expiresAt'];

const getCertificateEndDate = (cert) => {
  return CERT_DATE_FIELDS.reduce((foundDate, field) => {
    if (foundDate) return foundDate;
    const fieldValue = cert?.[field];
    if (!fieldValue) return null;
    const parsedDate = new Date(fieldValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }, null);
};

const isCertificateSelectable = (cert) => {
  if (!cert?.thumbprint) return false;
  if (cert?.isValid === false || cert?.isExpired === true) return false;
  const endDate = getCertificateEndDate(cert);
  if (endDate && endDate <= new Date()) return false;
  return true;
};

function BatchIcpSignatureConfirmationPage(props) {
  const { batchKey } = useParams();
  const navigate = useNavigate();
  const { getSession } = useContext(AccountContext);

  const {
    requestBatchEndorsementIcpToken,
    sendBatchEndorsementIcpSignature,
    getBatchSignatureDetails,
  } = useContext(EntityContext);

  const [certificates, setCertificates] = useState([]);
  const [selectedCert, setSelectedCert] = useState('');
  const [loading, setLoading] = useState(true);
  const [batchDetails, setBatchDetails] = useState(null);
  const [batchTokens, setBatchTokens] = useState(null);
  const [signingProgress, setSigningProgress] = useState({
    isSigning: false,
    isGeneratingTokens: false,
    currentStep: 'idle',
    results: null
  });
  const [error, setError] = useState(null);
  const [bearerToken, setBearerToken] = useState(null);

  const navigateToSignatureFinished = useCallback(() => {
    navigate(`/signature/finish`);
  }, [navigate]);

  useEffect(() => {
    if (!batchKey) return undefined;
    let cancelled = false;

    const initializePage = async () => {
      try {
        let jwt = null;
        try {
          const session = await getSession();
          jwt = session?.idToken?.jwtToken ?? null;
        } catch {
          jwt = null;
        }
        if (cancelled) return;
        setBearerToken(jwt);

        const batchData = await getBatchSignatureDetails(jwt || undefined, batchKey);
        if (cancelled) return;

        if (batchData && batchData.error) {
          setError('Erro ao carregar detalhes do lote');
          setLoading(false);
          return;
        }
        setBatchDetails(batchData);

        const pki = createWebPKI();
        pki.init(() => {
          pki.listCertificates().success((certs) => {
            if (!cancelled) {
              const validCertificates = certs.filter(isCertificateSelectable);
              setCertificates(validCertificates);
              setLoading(false);
            }
          }).error(() => {
            if (!cancelled) {
              setError('Erro ao carregar certificados');
              setLoading(false);
            }
          });
        });
      } catch (err) {
        console.error('Erro na inicialização:', err);
        if (!cancelled) {
          setError('Erro na inicialização da página');
          setLoading(false);
        }
      }
    };

    initializePage();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getBatchSignatureDetails do contexto sem useCallback
  }, [batchKey, getSession]);

  const handleSelectCert = (event) => {
    setSelectedCert(event.target.value);
  };

  /** Doc: após tokens, assinar cada hash (Web PKI) e POST /endorsement/batch/icp/sign */
  const runBatchIcpSign = async (pki, tokensPayload) => {
    if (!tokensPayload?.tokens?.length) {
      throw new Error('Sem tokens para assinar');
    }
    if (!selectedCert) {
      throw new Error('Selecione um certificado');
    }

    await new Promise((resolve) => {
      pki.init(() => resolve());
    });

    const signaturePromises = tokensPayload.tokens.map(async (tokenData) => {
      const digestAlgorithm = digestAlgForWebPki(tokenData.alg);
      const signature = await new Promise((resolve, reject) => {
        pki.signHash({
          thumbprint: selectedCert,
          hash: tokenData.hash,
          digestAlgorithm,
        }).success(resolve).error(reject);
      });
      return {
        envelope_signer_key: tokenData.envelope_signer_key,
        signature,
      };
    });

    const signedRows = await Promise.all(signaturePromises);
    const signatures = signedRows.map((row) => ({
      envelope_signer_key: row.envelope_signer_key,
      signature: row.signature,
    }));

    const result = await sendBatchEndorsementIcpSignature(batchKey, signatures, bearerToken);
    if (!result) {
      throw new Error('Erro ao finalizar assinaturas em lote');
    }
    return result;
  };

  const applySignResult = (result) => {
    setSigningProgress((prev) => ({
      ...prev,
      isSigning: false,
      currentStep: 'completed',
      results: result,
    }));
    if (result.successful_signatures === result.total_processed) {
      setTimeout(() => {
        navigateToSignatureFinished();
      }, 3000);
    }
  };

  const generateBatchTokens = async () => {
    if (!selectedCert || !batchDetails) return;

    let tokensReceived = false;
    try {
      setSigningProgress((prev) => ({
        ...prev,
        isGeneratingTokens: true,
        currentStep: 'generating_tokens',
      }));
      setError(null);

      const pki = createWebPKI();
      const certEncoding = await new Promise((resolve, reject) => {
        pki.readCertificate({ thumbprint: selectedCert }).success(resolve).error(reject);
      });

      const tokensData = await requestBatchEndorsementIcpToken(batchKey, certEncoding, bearerToken);

      if (!tokensData?.tokens?.length) {
        throw new Error('Erro ao gerar tokens em lote');
      }

      setSigningProgress((prev) => ({
        ...prev,
        isGeneratingTokens: false,
        currentStep: 'tokens_generated',
      }));

      setSigningProgress((prev) => ({ ...prev, isSigning: true, currentStep: 'signing' }));
      setError(null);

      let result;
      try {
        result = await runBatchIcpSign(pki, tokensData);
      } catch (signErr) {
        setBatchTokens(tokensData);
        tokensReceived = true;
        setSigningProgress((prev) => ({
          ...prev,
          isSigning: false,
          currentStep: 'tokens_generated',
        }));
        throw signErr;
      }

      setBatchTokens(tokensData);
      tokensReceived = true;
      applySignResult(result);
    } catch (err) {
      console.error('Erro no fluxo ICP em lote:', err);
      setError(
        err.message?.includes('assinar') || err.message?.includes('finalizar')
          ? `Erro ao assinar documentos: ${err.message || 'Erro desconhecido'}`
          : `Erro ao gerar tokens em lote: ${err.message || 'Erro desconhecido'}`
      );
      setSigningProgress((prev) => ({
        ...prev,
        isGeneratingTokens: false,
        isSigning: false,
        currentStep: tokensReceived ? 'tokens_generated' : 'idle',
      }));
    }
  };

  const signBatchDocuments = async () => {
    if (!batchTokens) return;

    try {
      setSigningProgress((prev) => ({
        ...prev,
        isSigning: true,
        currentStep: 'signing',
      }));
      setError(null);

      const pki = createWebPKI();
      const result = await runBatchIcpSign(pki, batchTokens);
      applySignResult(result);
    } catch (err) {
      console.error('Erro ao assinar documentos:', err);
      setError(`Erro ao assinar documentos: ${err.message || 'Erro desconhecido'}`);
      setSigningProgress((prev) => ({
        ...prev,
        isSigning: false,
        currentStep: 'tokens_generated',
      }));
    }
  };

  const getStatusIcon = (envelopeSignerKey) => {
    if (!signingProgress.results) return <PendingIcon color="disabled" />;

    const result = signingProgress.results.results?.find(r => r.envelope_signer_key === envelopeSignerKey);
    if (!result) return <PendingIcon color="disabled" />;

    if (result.success) {
      return <CheckCircleIcon color="success" />;
    }
    return <ErrorIcon color="error" />;
  };

  const getStatusText = (envelopeSignerKey) => {
    if (!signingProgress.results) return 'Pendente';

    const result = signingProgress.results.results?.find(r => r.envelope_signer_key === envelopeSignerKey);
    if (!result) return 'Pendente';

    if (result.success) {
      return 'Assinado';
    }
    return result.error || 'Falhou';
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Stack alignItems="center" justifyContent="center" spacing={3} sx={{ mt: { xs: 2, sm: 4 }, mb: 8 }}>
          <CircularProgress />
          <Typography>Carregando...</Typography>
        </Stack>
      </Container>
    );
  }

  if (error && !batchDetails) {
    return (
      <Container maxWidth="md">
        <Stack alignItems="center" justifyContent="center" spacing={3} sx={{ mt: 8, mb: 8 }}>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Tentar Novamente
          </Button>
        </Stack>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Assinatura ICP em Lote</title>
      </Helmet>

      <Container maxWidth="md">
        <Stack spacing={4} sx={{ mt: { xs: 1, sm: 2 }, mb: 4 }}>
          <Typography variant="h4" gutterBottom align="center" sx={{ px: { xs: 0.5, sm: 0 } }}>
            Assinatura ICP em Lote
          </Typography>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {batchDetails && !bearerToken && (
            <Alert severity="warning">
              É necessário estar logado para gerar tokens e concluir a assinatura.
            </Alert>
          )}

          {batchDetails && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Detalhes do Lote
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total de envelopes:{' '}
                  {batchDetails.envelopes?.length ||
                    batchDetails.envelope_signer_keys?.length ||
                    0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Chave do lote: {batchKey}
                </Typography>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Selecione um Certificado
              </Typography>

              <FormControl fullWidth>
                <InputLabel id="cert-select-label">Certificado</InputLabel>
                <Select
                  labelId="cert-select-label"
                  id="cert-select"
                  value={selectedCert}
                  label="Certificado"
                  onChange={handleSelectCert}
                  disabled={signingProgress.isGeneratingTokens || signingProgress.isSigning}
                >
                  <MenuItem value="">Selecione um certificado</MenuItem>
                  {certificates.map((cert) => (
                    <MenuItem key={cert.thumbprint} value={cert.thumbprint}>
                      {cert.subjectName} (emitido por {cert.issuerName})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!certificates.length && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Nenhum certificado valido e nao expirado foi encontrado.
                </Alert>
              )}

              <Box sx={{ mt: 2 }}>
                {!batchTokens ? (
                  <Button
                    variant="contained"
                    onClick={generateBatchTokens}
                    disabled={
                      !selectedCert ||
                      signingProgress.isGeneratingTokens ||
                      signingProgress.isSigning ||
                      signingProgress.currentStep === 'completed'
                    }
                    fullWidth
                    startIcon={
                      signingProgress.isGeneratingTokens || signingProgress.isSigning ? (
                        <CircularProgress size={20} />
                      ) : (
                        <PlayArrowIcon />
                      )
                    }
                  >
                    {signingProgress.isGeneratingTokens
                      ? 'Gerando tokens...'
                      : signingProgress.isSigning
                        ? 'Assinando documentos...'
                        : 'Assinar'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={signBatchDocuments}
                    disabled={
                      signingProgress.isSigning || signingProgress.currentStep === 'completed'
                    }
                    fullWidth
                    startIcon={signingProgress.isSigning ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                  >
                    {signingProgress.isSigning ? 'Assinando...' : 'Tentar assinar novamente'}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>

          {signingProgress.isGeneratingTokens && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Gerando Tokens
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={24} />
                  <Typography>Gerando tokens para todos os envelopes...</Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          {signingProgress.isSigning && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Assinando Documentos
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={24} />
                  <Typography>Assinando todos os documentos com certificado digital...</Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          {signingProgress.results && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Resultados da Assinatura
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total processado: {signingProgress.results.total_processed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Assinaturas bem-sucedidas: {signingProgress.results.successful_signatures}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Assinaturas que falharam: {signingProgress.results.failed_signatures}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Envelopes finalizados: {signingProgress.results.finished_envelopes}
                  </Typography>
                </Box>

                <List dense>
                  {signingProgress.results.results?.map((result, index) => (
                    <div key={result.envelope_signer_key}>
                      <ListItem>
                        <ListItemIcon>
                          {getStatusIcon(result.envelope_signer_key)}
                        </ListItemIcon>
                        <ListItemText
                          primary={`Envelope ${result.envelope_key}`}
                          secondary={getStatusText(result.envelope_signer_key)}
                        />
                        {result.success && (
                          <Chip
                            label={result.envelope_finished ? "Finalizado" : "Assinado"}
                            color={result.envelope_finished ? "success" : "primary"}
                            size="small"
                          />
                        )}
                        {!result.success && (
                          <Chip label="Falhou" color="error" size="small" />
                        )}
                      </ListItem>
                      {index < (signingProgress.results.results?.length || 0) - 1 && <Divider />}
                    </div>
                  ))}
                </List>

                {signingProgress.results.successful_signatures === signingProgress.results.total_processed && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Todas as assinaturas foram realizadas com sucesso! Redirecionando...
                  </Alert>
                )}

                {signingProgress.results.failed_signatures > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {signingProgress.results.failed_signatures} assinatura(s) falharam. Verifique os detalhes acima.
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}
        </Stack>
      </Container>
    </>
  );
}

export default BatchIcpSignatureConfirmationPage;
